import React, { Component, createRef } from 'react';

import { GenericMouseEvent } from '../../../types/types';
import { UIUtils } from '../../../utils/dom-utils';
import { WindowUtils } from '../../../utils/window.utils';
import style from './seekbar.component.module.scss';


interface SeekbarProps {
    max?: number;
    min?: number;
    start?: number; // Initial value
    step?: number;
    emitInitialValueOnInit?: boolean;
    onChange?: (value: number) => void;
    onChangeEnd?: (value: number) => void;
    /**
     * UXFU2-01 / UXM-01: what the slider sets ("File size (%)"), when its label is drawn by the caller. It names the
     * handle, which is the slider a keyboard or screen-reader user operates.
     */
    ariaLabel?: string;
    /** UXM-01: how a screen reader reads the value ("80%"). Defaults to the number itself. */
    valueText?: (value: number) => string;
}

interface SeekbarState {
    value: number;
}

export default class Seekbar extends Component<SeekbarProps, SeekbarState> {
    containerRef = createRef<HTMLDivElement>();
    railRef = createRef<HTMLDivElement>();
    handlerRef = createRef<HTMLSpanElement>();

    private mouseMoveHandler: ((e: GenericMouseEvent) => void) | null = null;
    private removeCurrentMouseMoveHandler: ((e?: any) => void) | null = null;
    private railWidth: number = 0;
    private handlerWidth: number = 24;
    private halfHandlerWidth: number = this.handlerWidth / 2;

    private max: number = 100;
    private min: number = 0;
    private step: number = 1;
    private valueFormatter: (val: number) => number = (val: number) => val;

    constructor(props: SeekbarProps) {
        super(props);
        this.initializePropsAndState(props);
        this.state = {
            value: this.props.start ?? this.min,
        };
    }

    private initializePropsAndState(props: SeekbarProps) {
        this.max = props.max ?? 100;
        this.min = props.min ?? 0;
        this.step = props.step ?? 1;

        if (this.min > this.max) {
            console.warn("Seekbar: 'min' prop is greater than 'max'. Swapping values.");
            [this.min, this.max] = [this.max, this.min];
        }

        const stepStr = `${this.step}`;
        if (stepStr.includes('.')) {
            const numberOfDecimals = stepStr.length - (stepStr.indexOf('.') + 1);
            this.valueFormatter = (val) => parseFloat(val.toFixed(numberOfDecimals));
        } else {
            this.valueFormatter = (val) => Math.round(val / this.step) * this.step;
        }
    }

    componentDidMount() {
        if (this.railRef.current) {
            this.railWidth = this.railRef.current.offsetWidth;
        }

        if (this.props.emitInitialValueOnInit) {
            this.notifyCallbacks(this.state.value, 'change');
            this.notifyCallbacks(this.state.value, 'changeEnd');
        }
    }

    componentDidUpdate(prevProps: SeekbarProps) {
        if (this.railRef.current && this.railWidth !== this.railRef.current.offsetWidth) {
            this.railWidth = this.railRef.current.offsetWidth;
        }
        if (prevProps.max !== this.props.max || prevProps.min !== this.props.min || prevProps.step !== this.props.step || prevProps.start !== this.props.start) {
            this.initializePropsAndState(this.props);

            // Only use props.start if it specifically changed
            const newValue = (prevProps.start !== this.props.start && this.props.start !== undefined)
                ? this.props.start
                : this.state.value;

            this.setState({
                value: this.snapValueToStep(newValue),
            }, () => {
                this.notifyCallbacks(this.state.value, 'change');
            });
        }
    }

    componentWillUnmount() {
        if (this.removeCurrentMouseMoveHandler) {
            this.removeCurrentMouseMoveHandler();
            this.removeCurrentMouseMoveHandler = null;
        }
    }

    private handleHandlerInteraction = (e: React.MouseEvent | React.TouchEvent): void => {
        e.preventDefault();

        if (!this.railRef.current) return;

        this.railWidth = this.railRef.current.offsetWidth;

        if (!this.mouseMoveHandler) {
            this.mouseMoveHandler = (ev: GenericMouseEvent) => this.handleMouseMove(ev);
        }

        this.removeCurrentMouseMoveHandler = WindowUtils.addMoveMoveHandlerToWindow(
            this.mouseMoveHandler,
            true,
            this.handleMouseup
        );

        const event = UIUtils.toGenericMouseEvent(e);
        if (event) {
            const railRect = this.railRef.current.getBoundingClientRect();
            const mouseX = event.pageX - railRect.left;
            this.updateValueFromMousePosition(mouseX, true);
        }
    }

    private handleRailInteraction = (e: React.MouseEvent | React.TouchEvent): void => {
        e.preventDefault();

        if (!this.railRef.current) return;

        this.railWidth = this.railRef.current.offsetWidth;

        const event = UIUtils.toGenericMouseEvent(e);
        if (!event) return;

        const railRect = this.railRef.current.getBoundingClientRect();
        const mouseX = event.pageX - railRect.left;

        this.updateValueFromMousePosition(mouseX, true);

        // Continue with the drag setup after the initial tap/click
        this.handleHandlerInteraction(e);
    }

    private handleMouseMove = (event: GenericMouseEvent): void => {
        if (!this.railRef.current) return;

        const railRect = this.railRef.current.getBoundingClientRect();
        const mouseX = event.pageX - railRect.left;
        this.updateValueFromMousePosition(mouseX, true);
    }

    private handleMouseup = (): void => {
        if (this.removeCurrentMouseMoveHandler) {
            this.removeCurrentMouseMoveHandler = null;
        }
        this.notifyCallbacks(this.state.value, 'changeEnd');
    }

    private updateValueFromMousePosition(mouseX: number, notify: boolean = true): void {
        if (this.railWidth === 0) return;

        let desiredHandlerCenter = mouseX;

        if (desiredHandlerCenter < 0) {
            desiredHandlerCenter = 0;
        } else if (desiredHandlerCenter > this.railWidth) {
            desiredHandlerCenter = this.railWidth;
        }

        const percentage = desiredHandlerCenter / this.railWidth;
        const rawValue = this.min + percentage * (this.max - this.min);
        const snappedValue = this.snapValueToStep(rawValue);

        if (snappedValue !== this.state.value) {
            this.setState({ value: snappedValue });
            if (notify) {
                this.notifyCallbacks(snappedValue, 'change');
            }
        }
    }

    private snapValueToStep(rawValue: number): number {
        const steppedValue = Math.round((rawValue - this.min) / this.step) * this.step + this.min;
        // Clamp AFTER formatting: the formatter snaps to the step grid, so
        // clamping first could be undone by it. With min=1 step=5 (the 1-500
        // percent slider) dragging fully left gave round(1/5)*5 = 0 — below the
        // slider's own minimum, which then failed the notes' validity check and
        // blanked the entire estimate block.
        return Math.min(this.max, Math.max(this.min, this.valueFormatter(steppedValue)));
    }

    /**
     * UXM-01: the handle is a `role="slider"`, so it takes the keys a native range input does. Arrows move one step,
     * PageUp/PageDown about a tenth of the range, Home/End jump to the ends. A key is a whole gesture (there is no
     * drag to finish), so it reports both `change` and `changeEnd`, the same pair a click on the rail ends with. The
     * value goes through the same snap-and-clamp as a drag, so the posted values cannot differ from the mouse's.
     */
    private pageStep(): number {
        const tenth = Math.round((this.max - this.min) / 10 / this.step) * this.step;
        return Math.max(this.step, tenth);
    }

    private handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>): void => {
        const current = this.state.value;
        let target: number;
        switch (e.key) {
            case 'ArrowRight':
            case 'ArrowUp':
                target = current + this.step;
                break;
            case 'ArrowLeft':
            case 'ArrowDown':
                target = current - this.step;
                break;
            case 'PageUp':
                target = current + this.pageStep();
                break;
            case 'PageDown':
                target = current - this.pageStep();
                break;
            case 'Home':
                target = this.min;
                break;
            case 'End':
                target = this.max;
                break;
            default:
                return;
        }
        // The key is ours even at an end of the range: a page must not scroll under a slider that is at its max.
        e.preventDefault();
        const next = this.snapValueToStep(target);
        if (next === current) return;
        this.setState({ value: next });
        this.notifyCallbacks(next, 'change');
        this.notifyCallbacks(next, 'changeEnd');
    }

    private notifyCallbacks(value: number, type: 'change' | 'changeEnd'): void {
        if (type === 'change' && this.props.onChange) {
            this.props.onChange(value);
        } else if (type === 'changeEnd' && this.props.onChangeEnd) {
            this.props.onChangeEnd(value);
        }
    }

    render() {
        const percentage = ((this.state.value - this.min) / (this.max - this.min)) * 100;
        const valueText = this.props.valueText ? this.props.valueText(this.state.value) : `${this.state.value}`;

        return (
            <div ref={this.containerRef} className={style.container} data-jkey="seekbar-container">
                {/* UXM-01: the slider below carries min, value and max, so the drawn numbers are not read a second time. */}
                <div className={style.state} aria-hidden="true">
                    <div className={style.min}>{this.min}</div>
                    <div className={style.value} data-jkey="seekbar-value">{this.state.value}</div>
                    <div className={style.max}>{this.max}</div>
                </div>
                <div className={style['range-selector']}>
                    {/* NEW: Wrapper for larger tap area */}
                    <div
                        className={style['seekbar-track-area']}
                        onMouseDown={this.handleRailInteraction}
                        onTouchStart={this.handleRailInteraction}
                    >
                        {/* The actual rail (visual) */}
                        <div ref={this.railRef} className={style.rail} data-jkey="seekbar-rail">
                            <div className={style['rail-progress']} style={{ width: `${percentage}%` }}></div>
                            <span
                                ref={this.handlerRef}
                                className={style.handler}
                                style={{ left: `${percentage}%` }}
                                onTouchStart={this.handleHandlerInteraction}
                                onMouseDown={this.handleHandlerInteraction}
                                onKeyDown={this.handleKeyDown}
                                data-jkey="seekbar-handler"
                                role="slider"
                                tabIndex={0}
                                aria-label={this.props.ariaLabel || undefined}
                                aria-valuemin={this.min}
                                aria-valuemax={this.max}
                                aria-valuenow={this.state.value}
                                aria-valuetext={valueText}
                                aria-orientation="horizontal"
                            ></span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}